// =============================================================================
// FolderCreateModal.jsx — KlassDoc Sprint 10C H10 Part B2
// -----------------------------------------------------------------------------
// "Nouveau dossier" / "New Folder" dialogue with Path 2 permissions picker.
//
// BEHAVIOR
// - Parent selector: choose root (top-level) or any existing folder as parent.
// - When parent = null (top-level folder):
//     Permissions section is shown. Creator + Administrators group are
//     auto-added, pinned at Full Access. User can add more principals
//     with any access level.
// - When parent is set (subfolder):
//     Permissions section replaced with an inheritance note pointing at
//     the top-level ancestor. Subfolders do not carry independent ACL.
//
// API CONTRACT
//   POST /api/folders { name, parent_id, color, permissions: [...] }
// The backend silently ignores `permissions` when parent_id is set
// (enforced by routes/folders.js) — this is defense in depth.
//
// PROPS
//   open, onClose, onSuccess
//   allFolders        : full folder list for the parent selector and
//                       top-level name lookup on inheritance note
//   defaultParentId   : preselected parent (e.g. current folder in
//                       FoldersPage). Pass null for a clean top-level.
//   hideParentSelect  : if true, hide the parent picker entirely
//                       (caller has already decided). Used by the
//                       FoldersPage inline create where the parent
//                       is implicit from navigation state.
//   api, lang, THEME, currentUser
// =============================================================================

import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "motion/react";
import * as lucide from "lucide-react";
import { PrincipalPicker, AclEntryRow } from "./security/SecurityControls";

function fmt(lang, fr, en) { return lang === "fr" ? fr : en; }

export default function FolderCreateModal({ open, onClose, onSuccess, allFolders = [], defaultParentId = null, hideParentSelect = false, api, lang = "fr", THEME, currentUser }) {
  const [name, setName] = useState("");
  const [parentId, setParentId] = useState(defaultParentId || "");
  const [permissions, setPermissions] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [groupsLoaded, setGroupsLoaded] = useState(false);

  // Reset state whenever the modal opens / parent default changes
  useEffect(() => {
    if (!open) return;
    setName("");
    setParentId(defaultParentId || "");
    setPermissions([]);
    setError(null);
    setSubmitting(false);
    setGroupsLoaded(false);
  }, [open, defaultParentId]);

  // Auto-seed permissions for top-level: creator (full, locked) +
  // Administrators group (full, locked). We only add the admin group
  // if it actually exists in the org — the safety-net still covers
  // us if it doesn't.
  const isTopLevel = !parentId;
  useEffect(() => {
    if (!open || !isTopLevel || groupsLoaded) return;
    let cancelled = false;
    (async () => {
      try {
        const gRes = await api.get("/groups");
        if (cancelled) return;
        const adminGroup = (gRes.data || []).find(g => g.name === "administrators");
        const seeded = [];
        if (currentUser?.id) {
          seeded.push({
            principal_type: "user",
            principal_id: currentUser.id,
            principal_name: currentUser.name || fmt(lang, "Vous", "You"),
            access_level: "full",
            locked: true,
          });
        }
        if (adminGroup) {
          seeded.push({
            principal_type: "group",
            principal_id: adminGroup.id,
            principal_name: adminGroup.name_fr || adminGroup.name_en || "Administrators",
            access_level: "full",
            locked: true,
          });
        }
        setPermissions(seeded);
      } catch (e) {
        console.error("Load groups for folder-create:", e);
        // Non-fatal — safety-net on backend still grants creator access.
      } finally {
        if (!cancelled) setGroupsLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [open, isTopLevel, groupsLoaded, api, currentUser, lang]);

  // When switching to a subfolder parent, discard permissions (they'd be ignored anyway)
  useEffect(() => {
    if (!isTopLevel) setPermissions([]);
  }, [isTopLevel]);

  useEffect(() => {
    function onKey(e) { if (e.key === "Escape" && !submitting) onClose(); }
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, submitting]);

  const topLevelAncestor = useMemo(() => {
    if (isTopLevel || !parentId) return null;
    // Walk up the parent chain via allFolders
    let cur = allFolders.find(f => String(f.id) === String(parentId));
    let safety = 0;
    while (cur?.parent_id && safety < 32) {
      const next = allFolders.find(f => String(f.id) === String(cur.parent_id));
      if (!next) break;
      cur = next;
      safety++;
    }
    return cur || null;
  }, [parentId, allFolders, isTopLevel]);

  const excludeIds = new Set(permissions.map(e => `${e.principal_type}-${e.principal_id}`));

  function addPrincipal(p) { setPermissions(prev => [...prev, p]); }
  function updatePermissionLevel(idx, level) {
    setPermissions(prev => prev.map((e, i) => i === idx ? { ...e, access_level: level } : e));
  }
  function removePermission(idx) {
    setPermissions(prev => prev.filter((_, i) => i !== idx));
  }

  async function handleSubmit() {
    if (!name.trim() || submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const body = { name: name.trim(), parent_id: parentId || null, color: "#D4915E" };
      if (isTopLevel) {
        body.permissions = permissions.filter(e => e.principal_id).map(e => ({
          principal_type: e.principal_type,
          principal_id: e.principal_id,
          principal_name: e.principal_name,
          access_level: e.access_level,
        }));
      }
      const created = await api.post("/folders", body);
      onSuccess?.(created);
    } catch (e) {
      console.error("Create folder error:", e);
      setError(e.message || fmt(lang, "Échec de la création", "Create failed"));
    } finally {
      setSubmitting(false);
    }
  }

  const FONT = "'DM Sans','Satoshi',-apple-system,sans-serif";
  const labelStyle = { display: "block", fontSize: 11, fontWeight: 600, color: THEME.textMuted, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.3 };
  const inputStyle = { width: "100%", padding: "10px 14px", background: THEME.surface, border: `1px solid ${THEME.border}`, borderRadius: 8, color: THEME.text, fontSize: 13, fontFamily: FONT, outline: "none" };

  if (!open) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: FONT }}
      onClick={e => { if (e.target === e.currentTarget && !submitting) onClose(); }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 10 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }}
        style={{ background: THEME.surfaceRaised, border: `1px solid ${THEME.border}`, borderRadius: 14, width: "100%", maxWidth: 560, maxHeight: "88vh", display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 24px 80px rgba(0,0,0,0.5)" }}
      >
        {/* HEADER */}
        <div style={{ padding: "20px 28px", borderBottom: `1px solid ${THEME.border}`, display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: THEME.infoDim, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <lucide.FolderPlus size={20} color={THEME.info} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: THEME.text }}>
              {fmt(lang, "Nouveau dossier", "New Folder")}
            </div>
            <div style={{ fontSize: 12, color: THEME.textDim, marginTop: 2 }}>
              {isTopLevel
                ? fmt(lang, "Définissez les permissions pour ce nouveau dossier racine", "Set permissions for this new top-level folder")
                : fmt(lang, "Créez un sous-dossier", "Create a subfolder")}
            </div>
          </div>
          <button onClick={() => !submitting && onClose()} disabled={submitting} style={{ background: "transparent", border: "none", cursor: submitting ? "not-allowed" : "pointer", padding: 6 }}>
            <lucide.X size={20} color={THEME.textMuted} />
          </button>
        </div>

        {/* CONTENT */}
        <div style={{ flex: 1, overflow: "auto", padding: "20px 28px" }}>
          {error && (
            <div style={{ padding: "10px 14px", marginBottom: 14, background: THEME.dangerDim, border: `1px solid ${THEME.danger}40`, borderRadius: 8, color: THEME.danger, fontSize: 12, display: "flex", alignItems: "center", gap: 8 }}>
              <lucide.AlertCircle size={14} />{error}
            </div>
          )}

          {/* Name field */}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>{fmt(lang, "Nom du dossier", "Folder Name")}</label>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              onKeyDown={e => e.key === "Enter" && !submitting && handleSubmit()}
              placeholder={fmt(lang, "Ex: Contrats 2026", "Ex: Contracts 2026")}
              autoFocus
              style={inputStyle}
            />
          </div>

          {/* Parent selector (optional) */}
          {!hideParentSelect && (
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>{fmt(lang, "Dossier parent", "Parent Folder")}</label>
              <select value={parentId} onChange={e => setParentId(e.target.value)} style={{ ...inputStyle, cursor: "pointer" }}>
                <option value="">{fmt(lang, "— Racine (dossier principal) —", "— Root (top level) —")}</option>
                {(() => {
                  function buildOpts(pid, depth) {
                    return allFolders
                      .filter(f => (f.parent_id || null) === (pid || null))
                      .flatMap(f => [{ id: f.id, label: "\u00A0\u00A0".repeat(depth) + (depth > 0 ? "└ " : "") + f.name }, ...buildOpts(f.id, depth + 1)]);
                  }
                  return buildOpts(null, 0).map(f => <option key={f.id} value={f.id}>{f.label}</option>);
                })()}
              </select>
            </div>
          )}

          {/* Permissions section — top-level only */}
          {isTopLevel ? (
            <div style={{ marginTop: 20, paddingTop: 18, borderTop: `1px solid ${THEME.borderSubtle}` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                <lucide.Shield size={14} color={THEME.warning} />
                <div style={{ fontSize: 13, fontWeight: 700, color: THEME.text }}>
                  {fmt(lang, "Permissions du dossier", "Folder Permissions")}
                </div>
              </div>

              <div style={{ padding: 12, marginBottom: 14, background: THEME.accentDim, border: `1px solid ${THEME.accent}40`, borderRadius: 8, display: "flex", alignItems: "flex-start", gap: 10 }}>
                <lucide.Info size={14} color={THEME.accent} style={{ flexShrink: 0, marginTop: 2 }} />
                <div style={{ fontSize: 12, color: THEME.textDim, lineHeight: 1.5 }}>
                  {fmt(lang,
                    "Les permissions s'appliquent à tout le contenu du dossier (sous-dossiers et documents). Les utilisateurs non listés n'auront aucun accès.",
                    "Permissions apply to all contents (subfolders and documents). Users not listed will have no access.")}
                </div>
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>{fmt(lang, "Ajouter des utilisateurs/groupes", "Add Users/Groups")}</label>
                <PrincipalPicker api={api} lang={lang} THEME={THEME} onAdd={addPrincipal} excludeIds={excludeIds} />
              </div>

              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: THEME.textMuted, marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.3 }}>
                  {permissions.length} {fmt(lang, "principaux", "principals")}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {permissions.length === 0 ? (
                    <div style={{ padding: 20, textAlign: "center", color: THEME.textDim, fontSize: 12 }}>
                      {fmt(lang, "Aucun principal — seuls les administrateurs verront ce dossier.", "No principals — only administrators will see this folder.")}
                    </div>
                  ) : permissions.map((entry, idx) => (
                    <AclEntryRow
                      key={`${entry.principal_type}-${entry.principal_id}-${idx}`}
                      entry={entry}
                      onChangeAccess={lvl => updatePermissionLevel(idx, lvl)}
                      onRemove={() => removePermission(idx)}
                      lang={lang}
                      THEME={THEME}
                      readOnly={false}
                    />
                  ))}
                </div>
              </div>
            </div>
          ) : (
            /* Subfolder inheritance note */
            <div style={{ marginTop: 20, paddingTop: 18, borderTop: `1px solid ${THEME.borderSubtle}` }}>
              <div style={{ padding: 14, background: THEME.infoDim, border: `1px solid ${THEME.info}40`, borderRadius: 10, display: "flex", alignItems: "flex-start", gap: 12 }}>
                <div style={{ width: 32, height: 32, borderRadius: 8, background: THEME.info + "20", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <lucide.FolderTree size={16} color={THEME.info} />
                </div>
                <div style={{ flex: 1, fontSize: 12, color: THEME.textDim, lineHeight: 1.5 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: THEME.text, marginBottom: 3 }}>
                    {fmt(lang, "Permissions héritées", "Inherited Permissions")}
                  </div>
                  {topLevelAncestor ? (
                    <>
                      {fmt(lang, "Ce sous-dossier héritera des permissions de ", "This subfolder will inherit permissions from ")}
                      <strong style={{ color: THEME.text }}>{topLevelAncestor.name}</strong>
                      {fmt(lang, ". Les permissions de sous-dossiers ne peuvent pas être modifiées individuellement.", ". Subfolder permissions cannot be modified individually.")}
                    </>
                  ) : (
                    fmt(lang, "Ce sous-dossier héritera des permissions du dossier racine.", "This subfolder will inherit permissions from its top-level folder.")
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* FOOTER */}
        <div style={{ padding: "14px 28px", borderTop: `1px solid ${THEME.border}`, display: "flex", justifyContent: "flex-end", gap: 10, background: THEME.surface }}>
          <button type="button" onClick={onClose} disabled={submitting} style={{ padding: "9px 20px", background: "transparent", color: THEME.textMuted, border: `1px solid ${THEME.border}`, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: submitting ? "not-allowed" : "pointer", fontFamily: FONT }}>
            {fmt(lang, "Annuler", "Cancel")}
          </button>
          <button type="button" onClick={handleSubmit} disabled={submitting || !name.trim()} style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 20px", background: THEME.info, color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: (submitting || !name.trim()) ? "not-allowed" : "pointer", opacity: (submitting || !name.trim()) ? 0.55 : 1, fontFamily: FONT }}>
            {submitting ? <lucide.Loader2 size={14} className="spin" /> : <lucide.FolderPlus size={14} />}
            {fmt(lang, "Créer", "Create")}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
